import {
  Directive,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from "@angular/core";
import { SlickCarouselComponent } from "ngx-slick-carousel";
import { Subscription } from "rxjs";
import { AnalyticsService } from "../../services/analytics.service";

interface TrackableArticle {
  id: number | string;
}

/** The part of a slick carousel event this directive reads. */
interface SlickEvent {
  slick?: { options?: { slidesToShow?: number } };
  currentSlide?: number;
}

/**
 * Sends `resource_banner_*` events for the REsource articles of a carousel:
 * one impression per article the first time it is shown, and one click per
 * click through `trackClick`.
 *
 * <ngx-slick-carousel [resourceArticleTracking]="articles"
 *   trackingPlacement="featured_resource" trackingPageContext="property_page"
 *   #tracking="resourceArticleTracking">
 *   ... (click)="tracking.trackClick(article, i)"
 */
@Directive({
  selector: "ngx-slick-carousel[resourceArticleTracking]",
  exportAs: "resourceArticleTracking"
})
export class ResourceArticleTrackingDirective implements OnChanges, OnDestroy {
  @Input("resourceArticleTracking") articles: TrackableArticle[] = [];
  @Input() trackingPlacement: string = "";
  @Input() trackingPageContext: string = "";

  private slidesToShow: number = 1;
  private readonly seenIds = new Set<number | string>();
  private readonly subscriptions = new Subscription();

  constructor(
    private analyticsSrv: AnalyticsService,
    @Host() carousel: SlickCarouselComponent
  ) {
    this.subscriptions.add(
      carousel.init.subscribe((e: SlickEvent) => this.trackVisible(0, e))
    );
    this.subscriptions.add(
      carousel.afterChange.subscribe((e: SlickEvent) =>
        this.trackVisible(e.currentSlide, e)
      )
    );
  }

  // A carousel can be set up before its articles arrive, so a new list of
  // articles is counted from the start as well.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.articles) {
      this.seenIds.clear();
      this.trackVisible(0);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** @param index 0-based index of the clicked article. */
  trackClick(article: TrackableArticle, index: number): void {
    this.analyticsSrv.trackResourceArticleClick(this.eventData(article, index));
  }

  private trackVisible(firstIndex: number, e?: SlickEvent): void {
    this.slidesToShow = e?.slick?.options?.slidesToShow || this.slidesToShow;
    const visible = (this.articles || []).slice(
      firstIndex,
      firstIndex + this.slidesToShow
    );
    visible.forEach((article, i) => {
      if (this.seenIds.has(article.id)) return;
      this.seenIds.add(article.id);
      this.analyticsSrv.trackResourceArticleImpression(
        this.eventData(article, firstIndex + i)
      );
    });
  }

  private eventData(article: TrackableArticle, index: number) {
    return {
      articleId: article.id,
      placement: this.trackingPlacement,
      position: index + 1,
      pageContext: this.trackingPageContext
    };
  }
}
